import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { ArrowRight, MapPin, Phone, Mail, ExternalLink, FileText, ArrowLeft, Search, CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";

// List page: shows all jurisdictions
export function DocumentsList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jurisdictions/documents").then(({ data }) => setItems(data.jurisdictions)).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="San Diego County Building Documents Library — All 19 Jurisdictions"
        description="Verified permit portals, applications, and handout forms for every San Diego jurisdiction — from City of San Diego to unincorporated County. Direct links, health-checked monthly."
        path="/documents"
      />
      <Header />
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <div className="label-overline mb-3">Documents Library</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-3" style={{ color: "var(--bw-text-primary)" }}>
          Every San Diego jurisdiction.<br/>Every official form.
        </h1>
        <p className="text-lg mb-6 max-w-3xl leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
          Verified permit portals, applications, and handouts for all {items.length || 19} jurisdictions we cover.
        </p>
        <p className="text-base mb-10 max-w-3xl leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
          Every link on this page is scraped and health-checked monthly. If a city's portal is down or a form has moved,
          you'll see a warning badge. Choose your jurisdiction below to see the exact application, plan review, inspection,
          and fee documents you'll need — whether you're building an ADU in Chula Vista, a deck in La Jolla, or a
          retaining wall in unincorporated San Diego County.
        </p>

        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a city…"
            className="bw-input pl-10"
            data-testid="docs-search"
          />
        </div>

        {loading ? (
          <div className="label-overline">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((j) => (
              <Link
                key={j.name}
                to={`/documents/${encodeURIComponent(j.name)}`}
                className="bw-card p-5 hover:border-brand transition-all block"
                style={{ ["--tw-border-opacity"]: 1 }}
                data-testid={`doc-jurisdiction-${j.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display font-bold text-lg leading-tight" style={{ color: "var(--bw-text-primary)" }}>
                    {j.name}
                  </div>
                  <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
                </div>
                <div className="text-xs mb-3" style={{ color: "var(--bw-text-secondary)" }}>
                  {j.portal_name}
                </div>
                <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--bw-brand)" }}>
                  {j.doc_count} documents
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Detail page: one jurisdiction
export function JurisdictionDetail() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [allJurisdictions, setAllJurisdictions] = useState([]);

  useEffect(() => {
    api.get(`/jurisdictions/${encodeURIComponent(name)}/documents`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Not found"));
    api.get("/jurisdictions/documents")
      .then(({ data }) => setAllJurisdictions(data.jurisdictions || []))
      .catch(() => {});
  }, [name]);

  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
        <Header />
        <div className="max-w-4xl mx-auto p-8">
          <div className="text-sm" style={{ color: "var(--bw-error)" }}>{error}</div>
          <Link to="/documents" className="btn-outline mt-4 inline-flex">Back</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
        <Header />
        <div className="max-w-4xl mx-auto p-8"><div className="label-overline">Loading…</div></div>
      </div>
    );
  }

  // Group by category
  const grouped = data.documents.reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title={`${data.name} Building Permits & Forms — BuildWise AI`}
        description={`Official ${data.name} building permit portal, applications, and inspection forms. ${data.documents.length} verified documents covering ADUs, decks, remodels, and more.`}
        path={`/documents/${encodeURIComponent(data.name)}`}
      />
      <Header />
      <div className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <Link to="/documents" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline" data-testid="back-docs-list">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> All Jurisdictions
        </Link>

        <div className="label-overline mb-3">San Diego County Jurisdiction</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-6" style={{ color: "var(--bw-text-primary)" }} data-testid="jurisdiction-title">
          {data.name}
        </h1>

        <p className="text-base mb-8 max-w-3xl leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
          {data.name} building department oversees permits and inspections for residential and commercial projects in this jurisdiction.
          Below are the {data.documents.length} official forms, applications, and handouts most builders need — pulled directly
          from {data.portal_name}. Use these to prepare your submission package, or start a project to get an AI-curated
          checklist tailored to your specific address and project type.
        </p>

        {/* Info grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <a href={data.portal_url} target="_blank" rel="noopener noreferrer" className="bw-card p-5 hover:bg-tan transition-all block" data-testid="portal-link">
            <div className="label-overline mb-2">Official Portal</div>
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg" style={{ color: "var(--bw-brand)" }}>{data.portal_name}</div>
              <ExternalLink className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
            </div>
          </a>
          <div className="bw-card p-5">
            <div className="label-overline mb-2">Contact</div>
            <div className="space-y-1 text-sm" style={{ color: "var(--bw-text-secondary)" }}>
              <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-1 flex-shrink-0" strokeWidth={1.5} /> {data.address}</div>
              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {data.phone}</div>
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> <a href={`mailto:${data.email}`} className="underline">{data.email}</a></div>
            </div>
          </div>
        </div>

        {data.fallback_url && (
          <div className="mb-10 p-4 border-l-4 flex items-start gap-3" style={{ borderColor: "var(--bw-warning)", backgroundColor: "var(--bw-bg-tertiary)" }} data-testid="fallback-notice">
            <HelpCircle className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={1.5} style={{ color: "var(--bw-warning)" }} />
            <div className="text-sm" style={{ color: "var(--bw-text-primary)" }}>
              <strong>Having trouble opening a link?</strong> Some cities use strict security that may block links from certain networks or browsers. If a document doesn't load, try the{" "}
              <a href={data.fallback_url} target="_blank" rel="noopener noreferrer" className="underline font-bold" style={{ color: "var(--bw-brand)" }}>
                city's main website
              </a>{" "}
              directly and navigate to Building/Development Services.
            </div>
          </div>
        )}

        {/* Documents grouped by category */}
        <div className="label-overline mb-4">Documents & Forms ({data.documents.length})</div>
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, docs]) => (
            <div key={category}>
              <div className="text-xs uppercase font-bold tracking-widest mb-3" style={{ color: "var(--bw-accent)" }}>{category}</div>
              <div className="space-y-2">
                {docs.map((doc, i) => (
                  <a
                    key={`${doc.url}-${i}`}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bw-card p-4 flex items-center justify-between hover:border-black transition-all"
                    data-testid={`doc-${doc.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <FileText className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
                      <div className="flex-1">
                        <div className="font-semibold text-base flex items-center gap-2" style={{ color: "var(--bw-text-primary)" }}>
                          {doc.name}
                          <HealthBadge health={doc.health} />
                        </div>
                        {doc.required_for && !doc.required_for.includes("*") && (
                          <div className="text-xs mt-1" style={{ color: "var(--bw-text-tertiary)" }}>
                            For: {doc.required_for.join(", ")}
                          </div>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bw-card p-6" style={{ backgroundColor: "var(--bw-bg-tertiary)" }}>
          <div className="label-overline mb-2">Need a personalized checklist?</div>
          <p className="text-sm mb-4" style={{ color: "var(--bw-text-secondary)" }}>
            Start a project on BuildWise AI to get a curated list of only the documents you need for your specific project type at this address.
          </p>
          <Link to="/new" className="btn-primary text-sm inline-flex" data-testid="cta-start-project">
            Start Project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {allJurisdictions.length > 1 && (
          <div className="mt-12" data-testid="other-jurisdictions">
            <div className="label-overline mb-4">Explore Other San Diego Jurisdictions</div>
            <div className="flex flex-wrap gap-2">
              {allJurisdictions
                .filter((j) => j.name !== data.name)
                .map((j) => (
                  <Link
                    key={j.name}
                    to={`/documents/${encodeURIComponent(j.name)}`}
                    className="text-xs px-3 py-1.5 border font-semibold hover:bg-tan transition-all"
                    style={{ borderColor: "var(--bw-border)", color: "var(--bw-text-primary)" }}
                    data-testid={`cross-link-${j.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  >
                    {j.name}
                  </Link>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function HealthBadge({ health }) {
  if (!health || health.ok == null) {
    return (
      <span title="Not yet verified" className="inline-flex items-center" data-testid="health-unknown">
        <HelpCircle className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--bw-text-tertiary)" }} />
      </span>
    );
  }
  if (health.ok) {
    const ts = health.checked_at ? new Date(health.checked_at).toLocaleDateString() : "";
    return (
      <span title={`Verified ${ts}`} className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--bw-success)" }} data-testid="health-ok">
        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
      </span>
    );
  }
  return (
    <span title={`Broken (HTTP ${health.status_code || "?"})`} className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--bw-error)" }} data-testid="health-broken">
      <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
      <span className="font-bold">{health.status_code || "ERR"}</span>
    </span>
  );
}
